import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { User } from "../models/User";
import { useAppContext } from "./AppContext";

interface ProviderProps {
  children: React.ReactNode;
}

export interface UserProfileContextType {
  user?: User;
  availableStations?: number[];
}

/** Context implementation */
// eslint-disable-next-line react-refresh/only-export-components
export const UserProfileContext = createContext<UserProfileContextType>({});

export const UserProfileProvider = ({ children }: ProviderProps) => {
  const [user, setUser] = useState<User>();
  const [availableStations, setAvailableStations] = useState<number[]>([]);
  const {
    userContextToken: _userContextToken,
    userContextData,
    userAssignments,
  } = useAppContext();

  // Use assignments data from AppContext
  useEffect(() => {
    if (!userAssignments) {
      setUser(undefined);
      return;
    }

    const enrichedUser = userAssignments;
    if (userContextData) {
      if (!enrichedUser.firstName && userContextData.FirstName) {
        enrichedUser.firstName = userContextData.FirstName;
      }
      if (!enrichedUser.lastName && userContextData.LastName) {
        enrichedUser.lastName = userContextData.LastName;
      }
      if (!enrichedUser.contactEmail && userContextData.Email) {
        enrichedUser.contactEmail = userContextData.Email;
      }
      if (!enrichedUser.adoId && userContextData.OId) {
        enrichedUser.adoId = userContextData.OId;
      }
    }

    setUser(enrichedUser);
  }, [userAssignments, userContextData]);

  useEffect(() => {
    // NOTE: since the user context token/data is refetched in multiple scenarios, we need to
    //   ensure this isn't reset for no reason. Once refetching of that/those is removed, we might
    //   be able to remove this extra comparison logic; that needs confirmation via testing
    //   though...might be useful to just keep it here anyways though - I imagine there is / might
    //   be a scenario where we do want to refetch/refresh/etc. the user context token/data, in
    //   which case this logic would prevent unnecessary downstream actions.
    if (
      !!userContextData &&
      (availableStations.some((x) => !userContextData.StationIds.includes(x)) ||
        userContextData.StationIds.some((x) => !availableStations.includes(x)))
    ) {
      setAvailableStations(userContextData.StationIds);
    }
  }, [userContextData, availableStations]);

  const context = useMemo<UserProfileContextType>(
    () => ({ user, availableStations: availableStations }),
    [user, availableStations],
  );

  return (
    <UserProfileContext.Provider value={context}>
      {children}
    </UserProfileContext.Provider>
  );
};

// Hook to use the UserProfileContext
// eslint-disable-next-line react-refresh/only-export-components
export const useUserProfileContext = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error(
      "useUserProfileContext must be used within a UserProfileProvider",
    );
  }
  return context;
};
